import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { v4 as uuidv4 } from 'uuid';
import { IntlShape, useIntl } from "react-intl";
import { Backend, User } from "./backend";
import { ChatManager } from "./";
import { useAppDispatch, useAppSelector } from "../store";
import { setFlaggedMessage, clearFlaggedMessage, selectFlaggedMessage } from "../store/ui";
import { Message, Parameters } from "./chat/types";
import { useChat, UseChatResult } from "./chat/use-chat";
// TTS functionality removed
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { isProxySupported } from "./chat/openai";
// TTS functionality removed
import { HARDCODED_VALUES } from "../hardcoded-values";

export interface Context {
    authenticated: boolean;
    sessionExpired: boolean;
    chat: ChatManager;
    user: User | null;
    intl: IntlShape;
    id: string | undefined | null;
    currentChat: UseChatResult;
    isHome: boolean;
    isShare: boolean;
    generating: boolean;
    flaggedMessage: { content: string; message: string; categories: string; } | null;
    onNewMessage: (message?: string) => Promise<string | false>;
    regenerateMessage: (message: Message) => Promise<boolean>;
    editMessage: (message: Message, content: string) => Promise<boolean>;
}

const AppContext = React.createContext<Context>({} as any);

const chatManager = new ChatManager();
const backend = new Backend(chatManager);

let intl: IntlShape;

export function useCreateAppContext(): Context {
    const { id: _id } = useParams();
    const [nextID, setNextID] = useState(uuidv4());
    const id = _id ?? nextID;

    const dispatch = useAppDispatch();
    const flaggedMessage = useAppSelector(selectFlaggedMessage);

    intl = useIntl();
    
    const { pathname } = useLocation();
    const isHome = pathname === '/';
    const isShare = pathname.startsWith('/s/');

    const currentChat = useChat(chatManager, id, isShare);
    const [authenticated, setAuthenticated] = useState(backend?.isAuthenticated || false);
    const [wasAuthenticated, setWasAuthenticated] = useState(backend?.isAuthenticated || false);

    useEffect(() => {
        chatManager.on('y-update', update => backend?.receiveYUpdate(update));
        
        const handleFlaggedMessage = (flaggedData: { content: string; message: string; categories: string; }) => {
            dispatch(setFlaggedMessage(flaggedData));
        };
        
        chatManager.on('flagged-message', handleFlaggedMessage);
        
        return () => {
            chatManager.off('flagged-message', handleFlaggedMessage);
        };
    }, [dispatch]);

    const updateAuth = useCallback((authenticated: boolean) => {
        setAuthenticated(authenticated);
        if (authenticated && backend.user) {
            chatManager.login(backend.user.email || backend.user.id);
        }
        if (authenticated) {
            setWasAuthenticated(true);
            localStorage.setItem('registered', 'true');
        }
    }, []);

    useEffect(() => {
        updateAuth(backend?.isAuthenticated || false);
        backend?.on('authenticated', updateAuth);
        
        // Ensure backend session is fetched immediately
        if (backend) {
            backend.getSession();
        }
        
        return () => {
            backend?.off('authenticated', updateAuth)
        };
    }, [updateAuth]);

    const onNewMessage = useCallback(async (message?: string) => {
        // Clear any flagged message state when user sends a new message
        dispatch(clearFlaggedMessage());
        
        // TTS functionality removed
        
        if (isShare) {
            return false;
        }

        if (!message?.trim().length) {
            return false;
        }

        // Backend proxy handles API key, no need to check here
        if (!isProxySupported()) {
            console.error('OpenAI proxy not supported');
            return false;
        }

        const parameters: Parameters = {
            model: chatManager.options.getOption<string>('parameters', 'model', id),
            temperature: HARDCODED_VALUES.parameters.temperature,
            personality: chatManager.options.getOption<string>('parameters', 'personality', id),
            extreme: chatManager.options.getOption<boolean>('parameters', 'extreme', id),
        };

        if (id === nextID) {
            setNextID(uuidv4());
            // TTS functionality removed
        }

            chatManager.sendMessage({
                chatID: id,
                content: message.trim(),
            requestedParameters: parameters,
                parentID: currentChat.leaf?.id,
            });

        return id;
    }, [id, currentChat.leaf, isShare]);

    const regenerateMessage = useCallback(async (message: Message) => {
        resetAudioContext();

        if (isShare) {
            return false;
        }

        // Backend proxy handles API key, no need to check here
        if (!isProxySupported()) {
            console.error('BasedGPT proxy not supported');
            return false;
        }

        const parameters: Parameters = {
            model: chatManager.options.getOption<string>('parameters', 'model', id),
            temperature: HARDCODED_VALUES.parameters.temperature,
            personality: chatManager.options.getOption<string>('parameters', 'personality', id),
            extreme: chatManager.options.getOption<boolean>('parameters', 'extreme', id),
        };

        await chatManager.regenerate(message, parameters);

        return true;
    }, [isShare]);

    const editMessage = useCallback(async (message: Message, content: string) => {
        // TTS functionality removed
        
        if (isShare) {
            return false;
        }

        if (!content?.trim().length) {
            return false;
        }

        // Backend proxy handles API key, no need to check here
        if (!isProxySupported()) {
            console.error('OpenAI proxy not supported');
            return false;
        }

        const parameters: Parameters = {
            model: chatManager.options.getOption<string>('parameters', 'model', id),
            temperature: HARDCODED_VALUES.parameters.temperature,
            personality: chatManager.options.getOption<string>('parameters', 'personality', id),
            extreme: chatManager.options.getOption<boolean>('parameters', 'extreme', id),
        };

        if (id && chatManager.has(id)) {
            await chatManager.sendMessage({
                chatID: id,
                content: content.trim(),
                requestedParameters: parameters,
                parentID: message.parentID,
            });
        } else {
            const id = await chatManager.createChat();
            await chatManager.sendMessage({
                chatID: id,
                content: content.trim(),
                requestedParameters: parameters,
                parentID: message.parentID,
            });
        }

        return true;
    }, [id, isShare]);

    const generating = currentChat?.messagesToDisplay?.length > 0
        ? !currentChat.messagesToDisplay[currentChat.messagesToDisplay.length - 1].done
        : false;

    const context = useMemo<Context>(() => ({
        authenticated,
        sessionExpired: !authenticated && wasAuthenticated,
        id,
        user: backend.user,
        intl,
        chat: chatManager,
        currentChat,
        isHome,
        isShare,
        generating,
        flaggedMessage,
        onNewMessage,
        regenerateMessage,
        editMessage,
    }), [authenticated, wasAuthenticated, generating, flaggedMessage, onNewMessage, regenerateMessage, editMessage, currentChat, id, isHome, isShare, intl]);

    return context;
}

export function useAppContext() {
    return React.useContext(AppContext);
}

export function AppContextProvider(props: { children: React.ReactNode }) {
    const context = useCreateAppContext();
    return <AppContext.Provider value={context}>
            {props.children}
    </AppContext.Provider>;
}

function resetAudioContext() {
    // TTS functionality removed
}